/**
 * @file schemas.ts
 * @brief Zod schemas + TS types for the Super Admin → Analytics feature.
 *
 * @details
 * Mirrors the response shapes produced by the existing admin analytics
 * endpoints (no backend changes required for this feature):
 * - `getSessionStats`     → KPI summary
 * - `getSessionTrend`     → time-series (period, count, cost)
 * - `getRevenueStats`     → KPI summary (revenue + cost + profit)
 * - `getRevenueTrend`     → time-series (revenue + cost + profit)
 * - `getLanguageStats`    → list of language pairs with counts/duration
 * - `getSessionsPerUser`  → top-N users
 * - `getSessionsPerCompany` → top-N companies
 * - `getUserGrowthStats`     → user signups over time
 * - `getCompanyGrowthStats`  → company signups over time
 *
 * Postgres `COUNT/SUM` come back as strings, so we coerce to number
 * via a small helper. `passthrough()` is used everywhere so backend
 * additions don't break parsing.
 *
 * @references
 * - BACKEND/src/features/admin/service.js (analytics functions)
 * - BACKEND/src/features/admin/routes.js  (route shapes)
 *
 * @author DES
 * @date 26-05-2026
 */

import { z } from "zod";

// ----------------------------------------------------------------------
// Numeric coercion — Postgres aggregate columns ship as strings.
// ----------------------------------------------------------------------
const numericString = z.union([
  z.number(),
  z
    .string()
    .transform((v) => (v === "" ? 0 : Number(v)))
    .pipe(z.number()),
  z.null().transform(() => 0),
]);

// ----------------------------------------------------------------------
// Common params for date-range and group-by queries.
// `startDate` / `endDate` are ISO strings; `groupBy` defaults to "day"
// per service.js defaults.
// ----------------------------------------------------------------------
export type AnalyticsRange = {
  startDate?: string;
  endDate?: string;
  // Super-admin analytics can optionally scope the whole dashboard to a single
  // company (the company filter/picker). Omitted / empty = platform-wide.
  companyId?: string;
};

export type GroupBy = "day" | "week" | "month";

// ----------------------------------------------------------------------
// Session stats (KPI block)
// ----------------------------------------------------------------------
export const SessionStatsSchema = z
  .object({
    total_sessions: numericString.default(0),
    total_duration_seconds: numericString.default(0),
    total_cost_cents: numericString.default(0),
    avg_duration_seconds: numericString.default(0),
  })
  .passthrough();
export type SessionStats = z.infer<typeof SessionStatsSchema>;

// ----------------------------------------------------------------------
// Session trend (line chart)
// ----------------------------------------------------------------------
export const SessionTrendPointSchema = z.object({
  period: z.string(),
  session_count: numericString,
  total_cost_cents: numericString,
});
export const SessionTrendSchema = z.array(SessionTrendPointSchema);
export type SessionTrendPoint = z.infer<typeof SessionTrendPointSchema>;

// ----------------------------------------------------------------------
// Revenue stats (KPI block)
// ----------------------------------------------------------------------
// (24-08-2026) The platform feed spans tenants and therefore currencies. The
// flat fields now describe the LARGEST currency alone; `totals_by_currency`
// carries the full split so a caller can disclose the rest instead of summing
// minor units across ledgers and printing the result as GBP.
export const RevenueCurrencyTotalSchema = z
  .object({
    currency: z.string(),
    total_invoices: numericString.default(0),
    total_revenue_cents: numericString.default(0),
    avg_invoice_cents: numericString.nullable().optional(),
    total_cost_cents: numericString.default(0),
    total_profit_cents: numericString.default(0),
  })
  .passthrough();

export const RevenueStatsSchema = z
  .object({
    currency: z.string().optional(),
    totals_by_currency: z.array(RevenueCurrencyTotalSchema).optional(),
    total_invoices: numericString.default(0),
    total_revenue_cents: numericString.default(0),
    avg_invoice_cents: numericString.default(0),
    total_cost_cents: numericString.default(0),
    total_profit_cents: numericString.default(0),
  })
  .passthrough();
export type RevenueStats = z.infer<typeof RevenueStatsSchema>;

// ----------------------------------------------------------------------
// Revenue trend (line chart)
// ----------------------------------------------------------------------
export const RevenueTrendPointSchema = z.object({
  period: z.string(),
  /** Buckets are per (period, currency) since 24-08-2026. */
  currency: z.string().optional(),
  total_revenue_cents: numericString,
  total_cost_cents: numericString,
  total_profit_cents: numericString,
});
export const RevenueTrendSchema = z.array(RevenueTrendPointSchema);
export type RevenueTrendPoint = z.infer<typeof RevenueTrendPointSchema>;

// ----------------------------------------------------------------------
// Language usage rows
// ----------------------------------------------------------------------
export const LanguageStatRowSchema = z
  .object({
    source_language: z.string().nullable().optional(),
    target_language: z.string().nullable().optional(),
    source_locale: z.string().nullable().optional(),
    target_locale: z.string().nullable().optional(),
    session_count: numericString,
    total_duration_seconds: numericString,
  })
  .passthrough();
export const LanguageStatsSchema = z.array(LanguageStatRowSchema);
export type LanguageStatRow = z.infer<typeof LanguageStatRowSchema>;

// ----------------------------------------------------------------------
// Sessions per user (top-N table)
// ----------------------------------------------------------------------
export const SessionsPerUserRowSchema = z
  .object({
    user_id: z.string(),
    email: z.string().nullable().optional(),
    first_name: z.string().nullable().optional(),
    last_name: z.string().nullable().optional(),
    session_count: numericString,
    total_duration_seconds: numericString,
    total_cost_cents: numericString,
  })
  .passthrough();
export const SessionsPerUserSchema = z.array(SessionsPerUserRowSchema);
export type SessionsPerUserRow = z.infer<typeof SessionsPerUserRowSchema>;

// ----------------------------------------------------------------------
// Sessions per company (top-N table)
// ----------------------------------------------------------------------
export const SessionsPerCompanyRowSchema = z
  .object({
    company_id: z.string(),
    company_name: z.string().nullable().optional(),
    session_count: numericString,
    total_duration_seconds: numericString,
    total_cost_cents: numericString,
  })
  .passthrough();
export const SessionsPerCompanySchema = z.array(SessionsPerCompanyRowSchema);
export type SessionsPerCompanyRow = z.infer<typeof SessionsPerCompanyRowSchema>;

// ----------------------------------------------------------------------
// Growth — users & companies registered over time. Backend returns a
// `{ trends, totals }` envelope, where `trends` is the time-series and
// `totals` is the running grand totals (active / suspended / deleted).
// ----------------------------------------------------------------------
export const UserGrowthPointSchema = z
  .object({
    period: z.string(),
    count: numericString,
    active_count: numericString.optional(),
    suspended_count: numericString.optional(),
    users: numericString.optional(),
    admins: numericString.optional(),
    superadmins: numericString.optional(),
  })
  .passthrough();
export const UserGrowthSchema = z.object({
  trends: z.array(UserGrowthPointSchema),
  totals: z
    .object({
      total_users: numericString,
      total_active: numericString,
      total_suspended: numericString,
      total_deleted: numericString,
    })
    .passthrough(),
});
export type UserGrowthPoint = z.infer<typeof UserGrowthPointSchema>;
export type UserGrowth = z.infer<typeof UserGrowthSchema>;

export const CompanyGrowthPointSchema = z
  .object({
    period: z.string(),
    count: numericString,
    active_count: numericString.optional(),
    deleted_count: numericString.optional(),
  })
  .passthrough();
export const CompanyGrowthSchema = z.object({
  trends: z.array(CompanyGrowthPointSchema),
  totals: z
    .object({
      total_companies: numericString,
      total_active: numericString,
      total_deleted: numericString,
    })
    .passthrough(),
});
export type CompanyGrowthPoint = z.infer<typeof CompanyGrowthPointSchema>;
export type CompanyGrowth = z.infer<typeof CompanyGrowthSchema>;
